import {
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { DefaultButton, Modal, moneyFormat } from 'design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
// Models
import { Total } from 'data-layer-cr/dist/models';
import QuotationCoverage, {
  makeEmptyQuotationCoverage,
} from 'data-layer-cr/dist/models/QuotationCoverage';
import { CoverageCustomizable } from 'src/models/Quotation/CoverageCuztomization';
// Network
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { REQUEST_PRICING } from 'src/store/quotation/requestIdentifiers';
// Redux related
import { connect, ConnectedProps } from 'react-redux';
import {
  cleanTotalPricePreview,
  updateQuotationPrices,
} from 'src/store/quotation/actions';
import { RootState } from 'src/store/rootReducer';
// components
import CoverageSlider from './CoverageSlider';

const { formatMoney } = moneyFormat;

const useStyles = makeStyles(() => ({
  priceContainer: {
    background:
      'linear-gradient(180deg, #0D2E68 0%, #123E8A 52.08%, #1E59C0 100%)',
    borderRadius: '10px',
    padding: '24px 0px',
    boxShadow: '0px 5px 5px rgba(0, 0, 0, 0.08)',
    color: 'white',
    fontSize: 15,
    fontWeight: 400,
    textAlign: 'center',
    marginTop: 10,
  },
  priceText: {
    fontSize: 23,
    fontWeight: 900,
  },
  image: {
    marginTop: 10,
  },
  titleText: {
    fontSize: 18,
    fontWeight: 900,
  },
  subtitleText: {
    fontSize: 12,
    fontWeight: 400,
  },
  logo: { marginRight: 11 },
  buttonContainer: {
    paddingTop: 30,
    paddingBottom: 10,
  },
}));

export const PersonalizationModal = ({
  open,
  coverages,
  anualPrice,
  packageOption,
  moneyType,
  totalPricePreview,
  onCloseRequested,
  onChangeCoverageLimit,
  cleanTotalPricePreviewAction,
  updateQuotationPricesAction,
  currentSelectedPlan,
}: Props): JSX.Element => {
  const classes = useStyles();
  const [changedCoverages, setChangedCoverages] = useState<QuotationCoverage[]>(
    [],
  );
  const [previewPrice, setPreviewPrice] = useState(anualPrice);
  const requestingQuotationState = useNetworkObserver(REQUEST_PRICING);
  const isConsulting = useMemo(
    () => requestingQuotationState === 'requesting',
    [requestingQuotationState],
  );

  const handleChangeLimit = useCallback(
    (code: string, value: number) => {
      const newCoverage: QuotationCoverage = {
        ...makeEmptyQuotationCoverage(),
        coverageId: code,
        insuredValue: value,
      };
      const newCoverages: QuotationCoverage[] = [];
      let neverChanged = true;
      changedCoverages.forEach((itemCov) => {
        if (itemCov.coverageId === code) {
          neverChanged = false;
          newCoverages.push(newCoverage);
        } else {
          newCoverages.push(itemCov);
        }
      });
      if (neverChanged) {
        newCoverages.push(newCoverage);
      }
      setChangedCoverages(newCoverages);
      onChangeCoverageLimit(
        packageOption,
        newCoverages,
        false,
        currentSelectedPlan,
      );
    },
    [
      onChangeCoverageLimit,
      packageOption,
      changedCoverages,
      currentSelectedPlan,
    ],
  );

  const handleClose = useCallback(() => {
    cleanTotalPricePreviewAction();
    setChangedCoverages([]);
    setPreviewPrice(anualPrice);
    onCloseRequested();
  }, [cleanTotalPricePreviewAction, onCloseRequested, anualPrice]);

  const handleUpdateLimit = useCallback(() => {
    if (changedCoverages.length > 0) {
      updateQuotationPricesAction(
        changedCoverages,
        currentSelectedPlan,
        packageOption,
      );
    }
    cleanTotalPricePreviewAction();
    setChangedCoverages([]);
    onCloseRequested();
  }, [
    updateQuotationPricesAction,
    changedCoverages,
    onCloseRequested,
    cleanTotalPricePreviewAction,
    currentSelectedPlan,
    packageOption,
  ]);

  useEffect(() => {
    if (totalPricePreview.grandTotal > 0) {
      setPreviewPrice(totalPricePreview.grandTotal);
    }
  }, [totalPricePreview]);

  useEffect(() => {
    setPreviewPrice(anualPrice);
  }, [anualPrice]);

  return (
    <Modal
      data-testid="personalization-coverage-modal"
      title="COTIZACIÓN"
      open={open}
      maxWidth="sm"
      onCloseRequested={handleClose}
    >
      <Grid container justifyContent="center">
        <Grid container item xs={12} lg={9} spacing={2}>
          <Grid item xs={12}>
            <Grid container alignItems="center" justifyContent="center">
              <img
                className={classes.image}
                src="/svg/option_flexible.svg"
                alt="option flexible logo"
                data-testid="flexible-icon"
              />
            </Grid>
          </Grid>

          <Grid item xs={12} data-testid="price-box">
            <Grid container alignItems="center" justifyContent="center">
              <Grid item xs={12} className={classes.priceContainer}>
                {isConsulting && (
                  <CircularProgress size={27} data-testid="spinner" />
                )}
                {!isConsulting && (
                  <>
                    Precio &nbsp;
                    <span
                      data-testid="price-info"
                      className={classes.priceText}
                    >
                      {formatMoney(previewPrice, true, moneyType)}
                    </span>{' '}
                    &nbsp;/ anual
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} data-testid="coverage-title">
            <Box display="flex">
              <img
                src="/svg/coverage_shield.svg"
                alt="coverage_shield_icon"
                className={classes.logo}
                data-testid="icon-image"
              />
              <Box
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  flexDirection: 'column',
                  paddingTop: 20,
                }}
              >
                <Typography
                  className={classes.titleText}
                  data-testid="title-text"
                >
                  Coberturas
                </Typography>
                <Typography className={classes.subtitleText}>
                  Ajuste el precio variando la cobertura
                </Typography>
              </Box>
            </Box>
          </Grid>
          {coverages.map((coverage) => (
            <CoverageSlider
              data-testid={`coverage-slider-${coverage.code}`}
              key={coverage.code}
              onChangeCoverageLimit={handleChangeLimit}
              coverage={coverage}
              disableSlider={isConsulting}
            />
          ))}
        </Grid>
      </Grid>

      <Grid
        container
        justifyContent="center"
        className={classes.buttonContainer}
      >
        <Grid item xs={11} lg={6}>
          <DefaultButton
            data-testid="update-button"
            fullWidth
            type="button"
            onClick={handleUpdateLimit}
            disabled={isConsulting}
          >
            actualizar
          </DefaultButton>
        </Grid>
      </Grid>
    </Modal>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  totalPricePreview: Total;
} => ({
  totalPricePreview: state.quotation.totalPricePreview,
});

const reduxActions = {
  cleanTotalPricePreviewAction: cleanTotalPricePreview,
  updateQuotationPricesAction: updateQuotationPrices,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;
interface Props extends PropsFromRedux {
  open: boolean;
  coverages: CoverageCustomizable[];
  anualPrice: number;
  moneyType: string | undefined;
  packageOption: number;
  onCloseRequested: () => void;
  onChangeCoverageLimit: (
    modifiedPackage: number,
    modifiedCoverage: QuotationCoverage[],
    shouldUpdate: boolean,
    packageSelected: string,
  ) => void;
  currentSelectedPlan: string;
}

export default connector(PersonalizationModal);
